import { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, ExternalLink, Loader2, Search, ChevronRight,
  Users, Clock, CheckCircle2, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Working on it': 'bg-amber-100 text-amber-700',
  'Done': 'bg-emerald-100 text-emerald-700',
  'Stuck': 'bg-red-100 text-red-700',
  'Not Started': 'bg-surface-page text-text-tertiary',
};

const MondayBoardsView = ({ user }) => {
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [boardData, setBoardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [search, setSearch] = useState('');
  const [itemFilter, setItemFilter] = useState('all');

  const fetchBoards = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/monday/boards`);
      if (res.ok) {
        const data = await res.json();
        setBoards(data.boards || []);
        setConfigured(data.configured !== false);
      }
    } catch (e) { toast.error('Failed to load Monday boards'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBoards(); }, [fetchBoards]);

  const fetchBoardItems = async (board) => {
    setSelectedBoard(board);
    setBoardLoading(true);
    try {
      const res = await authFetch(`${API}/api/monday/boards/${board.id}`);
      if (res.ok) {
        const data = await res.json();
        setBoardData(data);
      }
    } catch (e) { toast.error('Failed to load board items'); }
    finally { setBoardLoading(false); }
  };

  const filteredBoards = boards.filter(b =>
    b.name?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusValue = (item) => {
    const statusCol = item.column_values?.find(c => c.type === 'status' || c.id === 'status');
    return statusCol?.text || '';
  };

  const getPersonValue = (item) => {
    const personCol = item.column_values?.find(c => c.type === 'people' || c.id === 'person');
    return personCol?.text || '';
  };

  const getDateValue = (item) => {
    const dateCol = item.column_values?.find(c => c.type === 'date' || c.id === 'date');
    return dateCol?.text || '';
  };

  const items = boardData?.items_page?.items || [];
  const groups = boardData?.groups || [];

  const filteredItems = items.filter(item => {
    if (itemFilter === 'all') return true;
    const status = getStatusValue(item);
    return status.toLowerCase().includes(itemFilter.toLowerCase());
  });

  const itemsByGroup = {};
  filteredItems.forEach(item => {
    const groupTitle = item.group?.title || 'No Group';
    if (!itemsByGroup[groupTitle]) itemsByGroup[groupTitle] = [];
    itemsByGroup[groupTitle].push(item);
  });

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="monday-loading">
      <Loader2 className="h-8 w-8 animate-spin text-brand" />
    </div>
  );

  if (!configured) return (
    <div className="p-8">
      <div className="max-w-md mx-auto text-center py-16">
        <LayoutDashboard size={48} className="mx-auto text-text-muted mb-4" />
        <h2 className="text-xl font-semibold text-text-primary mb-2">Monday.com Not Connected</h2>
        <p className="text-text-secondary text-sm mb-6">Connect your Monday.com workspace in Settings to view boards and tasks here.</p>
        <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="go-to-settings">
          Go to Settings
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="monday-boards-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-text-primary" data-testid="monday-page-title">Monday Boards</h2>
          <p className="text-text-secondary text-sm mt-1">{boards.length} active boards across your workspace</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchBoards} data-testid="refresh-boards">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
          <a href="https://bcm-teams.monday.com" target="_blank" rel="noopener noreferrer">
            <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" data-testid="open-monday">
              Open Monday.com <ExternalLink size={14} className="ml-1.5" />
            </Button>
          </a>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Board List (Left Panel) */}
        <div className="w-80 flex-shrink-0 space-y-3" data-testid="board-list-panel">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <Input
              placeholder="Search boards..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="board-search"
            />
          </div>

          <div className="space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto">
            {filteredBoards.map((board) => (
              <button
                key={board.id}
                onClick={() => fetchBoardItems(board)}
                className={`w-full text-left p-3 rounded-lg transition-all group ${
                  selectedBoard?.id === board.id
                    ? 'bg-brand/5 border border-brand/20'
                    : 'bg-white border border-border-subtle hover:border-brand/20 hover:bg-brand/5'
                }`}
                data-testid={`board-item-${board.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedBoard?.id === board.id ? 'bg-brand/10 text-brand' : 'bg-chart-purple/10 text-chart-purple'
                  }`}>
                    <LayoutDashboard size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      selectedBoard?.id === board.id ? 'text-brand' : 'text-text-primary'
                    }`}>{board.name}</p>
                    <p className="text-xs text-text-tertiary">
                      {board.items_count || 0} items
                      {board.workspace?.name ? ` · ${board.workspace.name}` : ''}
                    </p>
                  </div>
                  <ChevronRight size={14} className={`flex-shrink-0 transition-colors ${
                    selectedBoard?.id === board.id ? 'text-brand' : 'text-text-muted'
                  }`} />
                </div>
              </button>
            ))}
            {filteredBoards.length === 0 && (
              <p className="text-sm text-text-muted text-center py-8">No boards found</p>
            )}
          </div>
        </div>

        {/* Board Detail (Right Panel) */}
        <div className="flex-1 min-w-0" data-testid="board-detail-panel">
          {!selectedBoard ? (
            <div className="flex items-center justify-center h-64 border border-dashed border-border-subtle rounded-xl">
              <div className="text-center">
                <LayoutDashboard size={40} className="mx-auto text-text-muted mb-3" />
                <p className="text-text-secondary font-medium">Select a board</p>
                <p className="text-text-tertiary text-sm mt-1">Click a board from the left to view its items</p>
              </div>
            </div>
          ) : boardLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Board Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-text-primary" data-testid="board-detail-title">{selectedBoard.name}</h3>
                  <p className="text-sm text-text-tertiary mt-0.5">
                    {filteredItems.length} items
                    {groups.length > 0 && ` · ${groups.length} groups`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={itemFilter}
                    onChange={(e) => setItemFilter(e.target.value)}
                    className="text-sm border border-border-subtle rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-brand"
                    data-testid="item-filter"
                  >
                    <option value="all">All Items</option>
                    <option value="Working on it">Working on it</option>
                    <option value="Done">Done</option>
                    <option value="Stuck">Stuck</option>
                    <option value="Not Started">Not Started</option>
                  </select>
                  <a
                    href={`https://bcm-teams.monday.com/boards/${selectedBoard.id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs font-medium text-brand hover:text-brand-hover flex items-center gap-1 px-3 py-1.5 rounded-lg border border-brand/20 hover:bg-brand/5 transition-colors"
                  >
                    Open in Monday <ExternalLink size={12} />
                  </a>
                </div>
              </div>

              {/* Items by Group */}
              {Object.entries(itemsByGroup).length === 0 ? (
                <div className="text-center py-12 border border-dashed border-border-subtle rounded-xl">
                  <CheckCircle2 size={32} className="mx-auto text-text-muted mb-2" />
                  <p className="text-sm text-text-secondary">No items match the current filter</p>
                </div>
              ) : (
                Object.entries(itemsByGroup).map(([groupTitle, groupItems]) => {
                  const groupInfo = groups.find(g => g.title === groupTitle);
                  return (
                    <div key={groupTitle} className="rounded-xl border border-border-subtle bg-white overflow-hidden" data-testid={`group-${groupTitle}`}>
                      <div className="px-4 py-3 border-b border-border-subtle flex items-center gap-2"
                        style={groupInfo?.color ? { borderLeft: `4px solid ${groupInfo.color}` } : {}}>
                        <span className="text-sm font-semibold text-text-primary">{groupTitle}</span>
                        <span className="text-xs text-text-muted bg-surface-page px-2 py-0.5 rounded-full">{groupItems.length}</span>
                      </div>
                      <div className="divide-y divide-border-subtle">
                        {groupItems.map((item) => {
                          const status = getStatusValue(item);
                          const person = getPersonValue(item);
                          const date = getDateValue(item);
                          const statusClass = STATUS_COLORS[status] || 'bg-surface-page text-text-tertiary';

                          return (
                            <div key={item.id} className="px-4 py-3 flex items-center gap-4 hover:bg-surface-page/50 transition-colors" data-testid={`item-${item.id}`}>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-text-primary truncate">{item.name}</p>
                              </div>
                              {person && (
                                <div className="flex items-center gap-1.5 text-xs text-text-secondary flex-shrink-0">
                                  <Users size={12} />
                                  <span className="truncate max-w-[120px]">{person}</span>
                                </div>
                              )}
                              {date && (
                                <div className="flex items-center gap-1.5 text-xs text-text-secondary flex-shrink-0">
                                  <Clock size={12} />
                                  <span>{date}</span>
                                </div>
                              )}
                              {status && (
                                <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${statusClass}`}>
                                  {status}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MondayBoardsView;
